import { chatbotApi } from '../../../api/chatbotApi';
import { formatModeratorApiKeyError } from '../../../utils/apiClient';
import type { DataroomArticle } from '../../state/dataroomStore';

export const HAWK_DATAROOM_DRAG_MIME = 'application/x-hawk-dataroom-article';

export type AttachedChatArticle = {
  rawDataId: number;
  title: string;
  importId?: number;
  chunkCount?: number;
};

export function readDroppedArticleId(dataTransfer: DataTransfer): number | null {
  const typed = dataTransfer.getData(HAWK_DATAROOM_DRAG_MIME);
  const fallback = dataTransfer.getData('text/plain');
  const raw = typed || fallback;
  const articleId = Number(raw);
  if (!raw || Number.isNaN(articleId)) return null;
  return articleId;
}

export function setArticleDragData(dataTransfer: DataTransfer, articleId: number): void {
  const value = String(articleId);
  dataTransfer.setData(HAWK_DATAROOM_DRAG_MIME, value);
  dataTransfer.setData('text/plain', value);
  dataTransfer.effectAllowed = 'copyMove';
}

export async function ensureChatSession(sessionId: number | null): Promise<number> {
  if (sessionId) return sessionId;
  const res = await chatbotApi.createSession({ title: 'DataRoom assistant' });
  const id = res?.data?.id;
  if (!id) throw new Error('Could not create chat session');
  return id;
}

export async function importDroppedArticle(
  sessionId: number,
  article: DataroomArticle,
): Promise<AttachedChatArticle> {
  const res = await chatbotApi.importFromDataroom(sessionId, {
    raw_data_id: article.articleId,
  });
  const data = res?.data ?? {};
  return {
    rawDataId: article.articleId,
    title: data.title || article.title,
    importId: data.id,
    chunkCount: data.chunk_count ?? 0,
  };
}

export async function askChatbot(
  sessionId: number,
  question: string,
): Promise<string> {
  const res = await chatbotApi.ask(sessionId, { question });
  const answer = res?.data?.answer;
  if (typeof answer === 'string' && answer.trim()) return answer.trim();
  throw new Error('Assistant returned an empty reply');
}

export function formatChatError(err: unknown, fallback: string): string {
  return formatModeratorApiKeyError(err, fallback);
}
